"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: { label: "Back to top", text: "Back to top ↑" },
  ro: { label: "Înapoi sus", text: "Înapoi sus ↑" },
} as const;

type Props = {
  disabled?: boolean; // when Call menu open
  suspend?: boolean;  // while user scrolling
};

export default function BackToTop({ disabled, suspend }: Props) {
  const [visible, setVisible] = useState(false);
  const t = copy[useLocale()];
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };

    const onScroll = () => {
      // hide while scrolling
      setVisible(false);
      clear();

      // show after idle (page stable)
      timer.current = window.setTimeout(() => {
        const y = window.scrollY || 0;
        setVisible(y > 500);
      }, 550);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      clear();
    };
  }, []);

  const hidden = !!disabled || !!suspend || !visible;

  return (
    <button
      type="button"
      aria-label={t.label}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={[
        "fixed left-1/2 -translate-x-1/2 z-[9998] md:hidden",
        // keep it slightly above the bottom edge, but not colliding with WhatsApp
        "bottom-6",
        hidden
          ? "opacity-0 pointer-events-none translate-y-2"
          : "opacity-100 pointer-events-auto translate-y-0",
        "transition-all duration-300 ease-out",
        "rounded-full border border-white/10 bg-black/70 backdrop-blur-xl",
        "px-4 py-3 text-sm text-white shadow-2xl",
        "hover:bg-black/80 active:scale-[0.98]",
      ].join(" ")}
    >
      {t.text}
    </button>
  );
}
